import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, SectionCard, FormSection, Input, RadioGroup, Switch, Skeleton, RequiredMark,
  RichTextEditor,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useHomePopupQuery, useHomePopupMutation } from '../hooks/useHomePopupsQuery';
import { useHomePopupImageUpload } from '../hooks/useHomePopupImageUpload';
import {
  EMPTY_HOME_POPUP_FORM,
  buildHomePopupPayload,
  canSubmitHomePopup,
  formFromHomePopup,
  hasInvalidPeriod,
  type HomePopupFormState,
} from '../lib/homePopupForm';
import {
  HOME_POPUP_MALLS,
  HOME_POPUP_MALL_LABEL,
  POPUP_DEVICES,
  type HomePopupMall,
  type PopupDevice,
} from '../types/homePopup';
import { homePopupImageGuide } from '@/lib/upload/uploadGuide';

export const HomePopupFormPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { popupId: popupIdParam } = useParams<{ popupId: string }>();
  const isNew = popupIdParam === 'new' || popupIdParam == null;
  const popupId = isNew ? null : Number(popupIdParam);

  const query = useHomePopupQuery(popupId);
  const { create, update, remove } = useHomePopupMutation();
  const uploadImage = useHomePopupImageUpload();

  const [form, setForm] = useState<HomePopupFormState>(EMPTY_HOME_POPUP_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    setForm(formFromHomePopup(query.data));
  }, [query.data]);

  const set = <K extends keyof HomePopupFormState>(key: K, value: HomePopupFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleMall = (mall: HomePopupMall, checked: boolean) => {
    const next = new Set(form.visibleMalls);
    if (checked) next.add(mall);
    else next.delete(mall);
    // 표준 순서로 정규화해 페이로드를 안정적으로 유지한다.
    set('visibleMalls', HOME_POPUP_MALLS.filter((m) => next.has(m)));
  };

  const canSubmit = canSubmitHomePopup(form);
  const periodInverted = hasInvalidPeriod(form);
  const allMallsSelected = form.visibleMalls.length === HOME_POPUP_MALLS.length;

  const handleSubmit = async () => {
    setError(null);
    if (!canSubmit) {
      setError(t('homePopup.validation.required', '팝업 키·팝업명·본문은 필수이고, 노출 국가몰을 최소 1개 선택해야 합니다.'));
      return;
    }
    const payload = buildHomePopupPayload(form);
    try {
      if (isNew) {
        await create.mutateAsync(payload);
      } else if (popupId != null) {
        await update.mutateAsync({ id: popupId, payload });
      }
      navigate('/cms/home-popups');
    } catch (e) {
      setError(friendlyMessage(e, t('homePopup.saveFailed', '저장에 실패했습니다.')));
    }
  };

  const handleDelete = async () => {
    if (popupId == null) return;
    setError(null);
    try {
      await remove.mutateAsync(popupId);
      navigate('/cms/home-popups');
    } catch (e) {
      setError(friendlyMessage(e, t('homePopup.deleteFailed', '삭제에 실패했습니다.')));
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending;

  const breadcrumb = [
    { label: 'CMS', href: '/cms/home-popups' },
    { label: t('homePopup.title', '홈 팝업 관리'), href: '/cms/home-popups' },
    { label: isNew ? t('homePopup.create', '팝업 등록') : t('homePopup.edit', '수정') },
  ];

  const deviceOptions = POPUP_DEVICES.map((d) => ({
    value: d,
    label: t(`homePopup.device.${d}`, d),
  }));

  if (!isNew && query.isPending) {
    return (
      <PageShell title={t('homePopup.editTitle', '홈 팝업 수정')} breadcrumb={breadcrumb}>
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? t('homePopup.createTitle', '홈 팝업 등록') : t('homePopup.editTitle', '홈 팝업 수정')}
      breadcrumb={breadcrumb}
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              {t('homePopup.delete', '삭제')}
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || !canSubmit}>
            {t('homePopup.save', '저장')}
          </Button>
        </div>
      }
    >
      {error && (
        <div
          role="alert"
          className="rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {error}
        </div>
      )}

      <SectionCard title={t('homePopup.sectionBasic', '기본 정보')}>
        <div className="flex flex-col gap-3">
          <FormSection
            label={t('homePopup.fields.popupKey', '팝업 키')}
            density="compact"
            required
            /* 수정에서 키를 바꾸면 이미 "오늘 하루 안 보기"로 닫은 사용자에게 팝업이 다시 뜬다.
               서버도 수정 요청의 popupKey 를 무시하므로 읽기 전용으로 고정한다. */
            hint={
              isNew
                ? t('homePopup.hints.popupKeyNew', '스토어프론트의 "오늘 하루 안 보기" 저장 키입니다. 등록 후에는 변경할 수 없습니다.')
                : t('homePopup.hints.popupKeyEdit', '"오늘 하루 안 보기" 저장 키라 수정할 수 없습니다. 값을 바꾸면 이미 닫은 사용자에게 팝업이 다시 뜹니다.')
            }
          >
            <Input
              value={form.popupKey}
              onChange={(e) => set('popupKey', e.target.value)}
              maxLength={64}
              readOnly={!isNew}
              disabled={!isNew}
              required
              className="max-w-[360px]"
            />
          </FormSection>

          <FormSection label={t('homePopup.fields.title', '팝업명')} density="compact" required>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} required />
          </FormSection>

          <FormSection
            label={t('homePopup.fields.linkUrl', '링크 URL')}
            density="compact"
            hint={t('homePopup.hints.linkUrl', '팝업 클릭 시 이동할 주소입니다. 비우면 이동하지 않습니다.')}
          >
            <Input
              value={form.linkUrl}
              onChange={(e) => set('linkUrl', e.target.value)}
              maxLength={500}
              placeholder="https://"
            />
          </FormSection>

          <FormSection label={t('homePopup.fields.isActive', '활성화')} density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label={t('homePopup.fields.isActive', '활성화')}
            />
          </FormSection>

          <FormSection
            label={t('homePopup.fields.sortOrder', '노출 순서')}
            density="compact"
            hint={t('homePopup.hints.sortOrder', '작을수록 먼저 노출됩니다.')}
          >
            <Input
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
              className="max-w-[140px]"
            />
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title={<span>{t('homePopup.sectionContent', '본문')}<RequiredMark /></span>}>
        <RichTextEditor
          value={form.contentHtml}
          onChange={(html) => set('contentHtml', html)}
          onUploadImage={uploadImage}
          imageGuide={homePopupImageGuide(form.width, form.height)}
          onUploadError={(e) => setError(friendlyMessage(e, t('homePopup.imageUploadFailed', '이미지 업로드에 실패했습니다.')))}
          aria-label={t('homePopup.fields.contentHtml', '본문')}
        />
      </SectionCard>

      <SectionCard title={t('homePopup.sectionDisplay', '노출 조건')}>
        <div className="flex flex-col gap-3">
          <FormSection
            label={t('homePopup.fields.displayStartAt', '노출 시작')}
            density="compact"
            hint={t('homePopup.hints.displayStartAt', '비우면 시작 제약이 없습니다. 지정한 시각부터 노출됩니다.')}
          >
            <Input
              type="datetime-local"
              value={form.displayStartAt}
              onChange={(e) => set('displayStartAt', e.target.value)}
              className="max-w-[280px]"
            />
          </FormSection>

          <FormSection
            label={t('homePopup.fields.displayEndAt', '노출 종료')}
            density="compact"
            hint={t('homePopup.hints.displayEndAt', '비우면 상시 노출입니다. 지정한 시각 직전까지 노출됩니다(해당 시각 제외).')}
          >
            <Input
              type="datetime-local"
              value={form.displayEndAt}
              onChange={(e) => set('displayEndAt', e.target.value)}
              className="max-w-[280px]"
            />
          </FormSection>

          {periodInverted && (
            <p className="text-xs" style={{ color: 'var(--signal-danger)' }} role="alert">
              {t('homePopup.validation.periodInverted', '노출 종료가 시작보다 앞서거나 같습니다. 이대로 저장하면 팝업이 한 번도 노출되지 않습니다.')}
            </p>
          )}

          <FormSection label={t('homePopup.fields.device', '노출 기기')} density="compact">
            <RadioGroup
              value={form.device}
              onValueChange={(v) => set('device', v as PopupDevice)}
              options={deviceOptions}
              orientation="horizontal"
              aria-label={t('homePopup.fields.device', '노출 기기')}
            />
          </FormSection>

          <FormSection label={t('homePopup.fields.visibleMalls', '노출 국가몰')} density="compact" required>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {HOME_POPUP_MALLS.map((mall) => (
                  <label
                    key={mall}
                    className="inline-flex items-center gap-2 text-[14px]"
                    style={{ color: 'var(--content-default)' }}
                  >
                    <input
                      type="checkbox"
                      checked={form.visibleMalls.includes(mall)}
                      onChange={(e) => toggleMall(mall, e.target.checked)}
                      aria-label={t(`homePopup.malls.${mall}`, HOME_POPUP_MALL_LABEL[mall])}
                    />
                    {t(`homePopup.malls.${mall}`, HOME_POPUP_MALL_LABEL[mall])}
                  </label>
                ))}
              </div>
              <div className="text-xs" style={{ color: 'var(--content-muted)' }}>
                {allMallsSelected ? (
                  <span>{t('homePopup.hints.mallsAll', '전체 노출 (모든 국가몰에 노출)')}</span>
                ) : form.visibleMalls.length === 0 ? (
                  <span style={{ color: 'var(--signal-danger)' }}>
                    {t('homePopup.validation.mallsEmpty', '선택된 국가몰이 없습니다. 최소 1개를 선택하세요.')}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="underline"
                    onClick={() => set('visibleMalls', [...HOME_POPUP_MALLS])}
                    style={{ color: 'var(--accent-default)' }}
                  >
                    {t('homePopup.hints.mallsSelectAll', '전체 노출로 되돌리기')}
                  </button>
                )}
              </div>
            </div>
          </FormSection>

          <FormSection
            label={t('homePopup.fields.todayCloseEnabled', '오늘 하루 안 보기')}
            density="compact"
            hint={t('homePopup.hints.todayCloseEnabled', '켜면 스토어프론트 팝업에 "오늘 하루 안 보기" 버튼이 표시됩니다.')}
          >
            <Switch
              checked={form.todayCloseEnabled}
              onCheckedChange={(v) => set('todayCloseEnabled', v)}
              aria-label={t('homePopup.fields.todayCloseEnabled', '오늘 하루 안 보기')}
            />
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard
        title={t('homePopup.sectionGeometry', '크기·위치')}
        description={t('homePopup.hints.geometry', '모두 선택 입력입니다. 크기를 비우면 스토어프론트 기본 크기, 좌표를 비우면 중앙 정렬로 노출됩니다. 단위는 px 이며 음수는 저장되지 않습니다.')}
      >
        <div className="flex flex-wrap gap-3">
          <FormSection label={t('homePopup.fields.width', '가로(px)')} density="compact">
            <Input type="number" min={0} value={form.width} onChange={(e) => set('width', e.target.value)} className="max-w-[140px]" />
          </FormSection>
          <FormSection label={t('homePopup.fields.height', '세로(px)')} density="compact">
            <Input type="number" min={0} value={form.height} onChange={(e) => set('height', e.target.value)} className="max-w-[140px]" />
          </FormSection>
          <FormSection label={t('homePopup.fields.positionX', '좌측 offset(px)')} density="compact">
            <Input type="number" min={0} value={form.positionX} onChange={(e) => set('positionX', e.target.value)} className="max-w-[140px]" />
          </FormSection>
          <FormSection label={t('homePopup.fields.positionY', '상단 offset(px)')} density="compact">
            <Input type="number" min={0} value={form.positionY} onChange={(e) => set('positionY', e.target.value)} className="max-w-[140px]" />
          </FormSection>
        </div>
      </SectionCard>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('homePopup.deleteTitle', '홈 팝업 삭제')}</DialogTitle>
            <DialogDescription>
              {t('homePopup.deleteDesc', { title: form.title, defaultValue: '"{{title}}" 팝업을 삭제할까요? 되돌릴 수 없습니다.' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('homePopup.cancel', '취소')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              {t('homePopup.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
